import { z } from 'zod';
import { isRealDate, uuidSchema } from './common.js';
import { passwordSchema } from './auth.js';
const text = (max) => z.string().trim().max(max);
const optional = (max) => text(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine(isRealDate, 'Invalid calendar date');
const code = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_-]{0,19}$/, 'Code: 1-20 characters A-Z, 0-9, "_" or "-"');
const atLeastOne = (v) => Object.keys(v).length > 0;
// ------------------------------------------------------------------ terms
export const createTermSchema = z
    .object({ name: text(50).min(1), startDate: isoDate, endDate: isoDate })
    .refine((v) => v.endDate > v.startDate, {
    message: 'End date must be after start date',
    path: ['endDate'],
});
export const updateTermSchema = z
    .object({ name: text(50).min(1), startDate: isoDate, endDate: isoDate })
    .partial()
    .refine(atLeastOne, 'At least one field is required')
    .refine((v) => !v.startDate || !v.endDate || v.endDate > v.startDate, {
    message: 'End date must be after start date',
    path: ['endDate'],
});
// --------------------------------------------------------------- subjects
export const SUBJECT_TYPES = ['core', 'elective', 'co_curricular'];
export const createSubjectSchema = z.object({
    name: text(100).min(1),
    code,
    subjectType: z.enum(SUBJECT_TYPES).default('core'),
});
export const updateSubjectSchema = z
    .object({
    name: text(100).min(1),
    code,
    subjectType: z.enum(SUBJECT_TYPES),
    isActive: z.boolean(),
})
    .partial()
    .refine(atLeastOne, 'At least one field is required');
export const createSubjectGroupSchema = z.object({
    name: text(100).min(1),
    subjectIds: z.array(uuidSchema).max(100).default([]),
});
export const updateSubjectGroupSchema = z
    .object({ name: text(100).min(1), subjectIds: z.array(uuidSchema).max(100) })
    .partial()
    .refine(atLeastOne, 'At least one field is required');
export const setClassSubjectsSchema = z.object({
    subjects: z
        .array(z.object({ subjectId: uuidSchema, isOptional: z.boolean().default(false) }))
        .max(100),
});
// ---------------------------------------------------- streams and houses
export const createStreamSchema = z.object({ name: text(100).min(1) });
export const updateStreamSchema = createStreamSchema;
const colour = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a 6-digit hex colour like #1e3a8a');
export const createHouseSchema = z.object({
    name: text(50).min(1),
    color: colour.nullable().optional(),
});
export const updateHouseSchema = z
    .object({ name: text(50).min(1), color: colour.nullable() })
    .partial()
    .refine(atLeastOne, 'At least one field is required');
// ------------------------------------------------------------ teaching
export const setClassTeacherSchema = z.object({
    academicYearId: uuidSchema.optional(),
    staffId: uuidSchema,
});
export const createTeachingAssignmentSchema = z.object({
    academicYearId: uuidSchema.optional(),
    sectionId: uuidSchema,
    subjectId: uuidSchema,
    staffId: uuidSchema,
});
export const listTeachingQuerySchema = z.object({
    academicYearId: uuidSchema.optional(),
    sectionId: uuidSchema.optional(),
    staffId: uuidSchema.optional(),
});
export const yearQuerySchema = z.object({ academicYearId: uuidSchema.optional() });
export const createStaffLoginSchema = z.object({ password: passwordSchema });
// ------------------------------------------------------------- calendar
export const EVENT_TYPES = ['holiday', 'exam', 'event', 'meeting', 'break'];
export const createCalendarEventSchema = z
    .object({
    title: text(200).min(1),
    eventType: z.enum(EVENT_TYPES).default('event'),
    startDate: isoDate,
    endDate: isoDate.optional(),
    description: optional(1000),
})
    .transform((v) => ({ ...v, endDate: v.endDate ?? v.startDate }))
    .refine((v) => v.endDate >= v.startDate, {
    message: 'End date cannot be before start date',
    path: ['endDate'],
});
export const updateCalendarEventSchema = z
    .object({
    title: text(200).min(1),
    eventType: z.enum(EVENT_TYPES),
    startDate: isoDate,
    endDate: isoDate,
    description: optional(1000),
})
    .partial()
    .refine(atLeastOne, 'At least one field is required')
    .refine((v) => !v.startDate || !v.endDate || v.endDate >= v.startDate, {
    message: 'End date cannot be before start date',
    path: ['endDate'],
});
export const listCalendarQuerySchema = z.object({
    from: isoDate.optional(),
    to: isoDate.optional(),
    type: z.enum(EVENT_TYPES).optional(),
});
//# sourceMappingURL=academics.js.map